import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from './config';
import SearchableSelect from './components/SearchableSelect';

/**
 * WorkflowEnvVars — side panel for per-workflow settings:
 *   - Error Workflow override (which workflow to trigger on failure)
 *   - Environment variable overrides
 */
export default function WorkflowEnvVars({ workflowId, onClose }) {
  const [vars, setVars] = useState([]); // [{key, value, is_secret}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Error workflow state
  const [errorWorkflowId, setErrorWorkflowId] = useState('');
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [ewSaving, setEwSaving] = useState(false);
  const [ewSuccess, setEwSuccess] = useState(false);
  const [ewError, setEwError] = useState(null);

  // Checkpointing state
  const [checkpointingEnabled, setCheckpointingEnabled] = useState(false);
  const [cpSaving, setCpSaving] = useState(false);
  const [cpSuccess, setCpSuccess] = useState(false);
  const [cpError, setCpError] = useState(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  // Load workflow details + env vars + available workflows
  useEffect(() => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);

    const fetchWorkflow = fetch(`${API_BASE_URL}/workflows/${workflowId}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r));
    const fetchVars = fetch(`${API_BASE_URL}/workflows/${workflowId}/env-vars`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r));

    fetchWorkflow
      .then(wf => {
        setErrorWorkflowId(wf.error_workflow_id || '');
        setCheckpointingEnabled(Boolean(wf.checkpointing_enabled));
        // Fetch sibling workflows (same workspace or all personal)
        const wsParam = wf.workspace_id ? `?workspace_id=${wf.workspace_id}&limit=200` : '?limit=200';
        return fetch(`${API_BASE_URL}/workflows/${wsParam}`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : [])
          .then(list => setAvailableWorkflows(Array.isArray(list) ? list.filter(w => w.id !== workflowId) : []));
      })
      .catch(() => {});

    fetchVars
      .then(data => {
        setVars(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load variables.');
        setLoading(false);
      });
  }, [workflowId]);

  const addRow = () => {
    setVars(v => [...v, { key: '', value: '', is_secret: false }]);
  };

  const removeRow = (idx) => {
    setVars(v => v.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    setVars(v => v.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  const save = useCallback(async () => {
    // Validate: no empty keys, no duplicate keys
    const keys = vars.map(v => v.key.trim()).filter(Boolean);
    if (keys.length !== vars.length) {
      setError('All variable keys must be non-empty.');
      return;
    }
    if (new Set(keys).size !== keys.length) {
      setError('Duplicate variable keys are not allowed.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/workflows/${workflowId}/env-vars`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ variables: vars.map(v => ({ ...v, key: v.key.trim() })) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      const saved = await res.json();
      setVars(saved);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [vars, workflowId]);

  const saveErrorWorkflow = useCallback(async () => {
    setEwSaving(true);
    setEwError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/workflows/${workflowId}/error-workflow`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ error_workflow_id: errorWorkflowId || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      setEwSuccess(true);
      setTimeout(() => setEwSuccess(false), 2000);
    } catch (e) {
      setEwError(e.message);
    } finally {
      setEwSaving(false);
    }
  }, [errorWorkflowId, workflowId]);

  const saveCheckpointing = useCallback(async (enabled) => {
    setCpSaving(true);
    setCpError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/workflows/${workflowId}/checkpointing`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ checkpointing_enabled: enabled }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      setCheckpointingEnabled(enabled);
      setCpSuccess(true);
      setTimeout(() => setCpSuccess(false), 2000);
    } catch (e) {
      setCpError(e.message);
    } finally {
      setCpSaving(false);
    }
  }, [workflowId]);

  const inputStyle = {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    color: 'var(--text-primary)',
    fontSize: '12px',
    fontFamily: 'monospace',
    padding: '4px 6px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      width: '320px',
      background: 'var(--bg-secondary)',
      borderLeft: '1px solid var(--border-color)',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      boxSizing: 'border-box',
      flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'var(--bg-secondary)',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>
          Workflow Settings
        </span>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '16px', padding: '2px 6px', lineHeight: 1 }}
          title="Close"
        >✕</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>

        {/* ── Error Workflow ── */}
        <div style={{ marginBottom: '16px', paddingBottom: '14px', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
            Error Workflow Override
          </div>
          <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            When this workflow reaches a terminal <strong>failed</strong> state, trigger a separate error-handling workflow. Overrides the workspace default.
          </p>
          <SearchableSelect
            value={errorWorkflowId}
            onChange={setErrorWorkflowId}
            options={[
              { value: '', label: '— None (use workspace default) —' },
              ...availableWorkflows.map(w => ({ value: w.id, label: w.name })),
            ]}
            placeholder="— None (use workspace default) —"
            style={{ width: '100%', padding: '5px 6px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', fontSize: '12px', marginBottom: '8px' }}
          />
          {ewError && (
            <div style={{ marginBottom: '6px', padding: '4px 8px', background: 'rgba(244,67,54,0.1)', border: '1px solid var(--error-color,#f44336)', borderRadius: '4px', color: 'var(--error-color,#f44336)', fontSize: '11px' }}>
              {ewError}
            </div>
          )}
          <button
            onClick={saveErrorWorkflow}
            disabled={ewSaving}
            style={{ padding: '5px 12px', background: ewSuccess ? 'var(--success-color,#4caf50)' : 'var(--accent-color,#7b68ee)', color: '#fff', border: 'none', borderRadius: '4px', cursor: ewSaving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600, opacity: ewSaving ? 0.7 : 1 }}
          >
            {ewSuccess ? '✓ Saved' : ewSaving ? 'Saving…' : 'Save Error Workflow'}
          </button>
        </div>

        {/* ── Checkpointing ── */}
        <div style={{ marginBottom: '16px', paddingBottom: '14px', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
            Resume from Checkpoint
          </div>
          <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            When enabled, node outputs are saved after each step. If the run fails you can resume from any completed node without re-running earlier steps. Recommended for long workflows with LLM calls, API side effects, or approval chains.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: cpSaving ? 'not-allowed' : 'pointer', fontSize: '12px', color: 'var(--text-primary)', marginBottom: '8px' }}>
            <input
              type="checkbox"
              checked={checkpointingEnabled}
              disabled={cpSaving}
              onChange={e => saveCheckpointing(e.target.checked)}
              style={{ width: '14px', height: '14px' }}
            />
            Enable checkpointing
            {cpSaving && <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Saving…</span>}
            {cpSuccess && <span style={{ color: 'var(--success-color)' }}>✓ Saved</span>}
          </label>
          {cpError && (
            <div style={{ padding: '4px 8px', background: 'rgba(244,67,54,0.1)', border: '1px solid var(--error-color,#f44336)', borderRadius: '4px', color: 'var(--error-color,#f44336)', fontSize: '11px' }}>
              {cpError}
            </div>
          )}
        </div>

        {/* ── Environment Variables ── */}
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
          Environment Variables
        </div>
        <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Per-workflow overrides for <code style={{ fontSize: '10px', background: 'var(--bg-input)', padding: '1px 3px', borderRadius: '2px' }}>env</code> variables. Reference with <code style={{ fontSize: '10px', background: 'var(--bg-input)', padding: '1px 3px', borderRadius: '2px' }}>{"{{ env['KEY'] }}"}</code>.
        </p>
        {loading ? (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '12px', fontStyle: 'italic' }}>Loading…</div>
        ) : (
          <>
            {/* Column headers */}
            {vars.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px 24px', gap: '4px', marginBottom: '4px', paddingRight: '2px' }}>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Key</span>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Value</span>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>🔒</span>
                <span />
              </div>
            )}

            {/* Variable rows */}
            {vars.map((row, idx) => (
              <div
                key={idx}
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px 24px', gap: '4px', marginBottom: '6px', alignItems: 'center' }}
              >
                <input
                  style={inputStyle}
                  value={row.key}
                  onChange={e => updateRow(idx, 'key', e.target.value)}
                  placeholder="KEY"
                  spellCheck={false}
                />
                <input
                  style={inputStyle}
                  type={row.is_secret ? 'password' : 'text'}
                  value={row.value}
                  onChange={e => updateRow(idx, 'value', e.target.value)}
                  placeholder="value"
                  spellCheck={false}
                />
                <button
                  title={row.is_secret ? 'Mark as plain text' : 'Mark as secret (encrypted)'}
                  onClick={() => updateRow(idx, 'is_secret', !row.is_secret)}
                  style={{
                    background: row.is_secret ? 'var(--warning-color, #ff9800)' : 'var(--bg-hover, rgba(255,255,255,0.08))',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    color: row.is_secret ? '#fff' : 'var(--text-tertiary)',
                    cursor: 'pointer',
                    fontSize: '13px',
                    padding: '2px',
                    textAlign: 'center',
                    lineHeight: 1,
                  }}
                >
                  🔒
                </button>
                <button
                  title="Remove variable"
                  onClick={() => removeRow(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--error-color, #f44336)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: '0',
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}

            {/* Add row */}
            <button
              onClick={addRow}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: '1px dashed var(--border-color)',
                borderRadius: '4px',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '6px 10px',
                width: '100%',
                marginTop: vars.length > 0 ? '4px' : '0',
              }}
            >
              + Add Variable
            </button>
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          margin: '0 12px 8px',
          padding: '6px 10px',
          background: 'rgba(244, 67, 54, 0.1)',
          border: '1px solid var(--error-color, #f44336)',
          borderRadius: '4px',
          color: 'var(--error-color, #f44336)',
          fontSize: '11px',
          flexShrink: 0,
        }}>
          {error}
        </div>
      )}

      {/* Footer / Save */}
      <div style={{
        padding: '10px 12px',
        borderTop: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
      }}>
        <button
          onClick={save}
          disabled={saving || loading}
          style={{
            flex: 1,
            padding: '7px 12px',
            background: saveSuccess ? 'var(--success-color, #4caf50)' : 'var(--accent-color, #7b68ee)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: saving || loading ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            opacity: saving || loading ? 0.7 : 1,
            transition: 'background 0.2s',
          }}
        >
          {saveSuccess ? '✓ Saved' : saving ? 'Saving…' : 'Save Variables'}
        </button>
      </div>
    </div>
  );
}
